import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { EmailTemplateContentForm } from "@containers/modals/add-email-template-modal/email-template-content-form/email-template-content-form";
import { EmailTemplateMetadataForm } from "@containers/modals/add-email-template-modal/email-template-metadata-form/email-template-metadata-form";
import { EmailTemplateVariablesForm } from "@containers/modals/add-email-template-modal/email-template-variables-form/email-template-variables-form";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { EmailTemplate, FormattedEmailTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/email-template";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { createEmailTemplateReduxRequest } from "@redux-requests/email-templates";
import { getInitialEmailTemplate, removeUnusedVariablesFromEmailTemplateLanguageFiles } from "@utils/email-template/email-template-util";
import { getEmailTemplateValidationErrors } from "@utils/validation/email-template/email-template-validation-util";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

type AddEmailTemplateModalProps = {
    session: IDWSession;
    onModalClose: () => void;
    emailTemplates: Array<FormattedEmailTemplate>;
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
    serviceOrganizationId: string;
    addToReferenceData: boolean;
    onEmailTemplateCreated?: (emailTemplate: EmailTemplate) => void;
};

export const AddEmailTemplateModal = (props: AddEmailTemplateModalProps) => {
    const dispatch = useDispatch();
    const {
        session,
        onModalClose,
        axnCompanies,
        selfServiceVerificationWhiteLabeledEmails,
        emailTemplates,
        onEmailTemplateCreated,
        serviceOrganizationId,
        addToReferenceData
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [modalStep, setModalStep] = useState(0);

    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(LocaleTypes.ENGLISH);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [state, setState] = useState<FormattedEmailTemplate>(getInitialEmailTemplate(
        axnCompanies,
        false,
        session,
    ));

    useEffect(() => {
        const languages: Partial<MultiLanguageWrapper<{ [key: string]: any }>> = {};
        if (state.languages) {
            (Object.keys(state.languages) as Array<LocaleTypes>).forEach(locale => {
                languages[locale] = state.languages[locale] || {};
            });
        }

        setState({
            ...state,
            languages
        });
    }, [state.template]);

    const handleSubmitOnStep0 = (event: FormEvent) => {
        event.preventDefault();
        setModalStep(1);
    };

    const handleSubmitOnStep1 = (event: FormEvent) => {
        event.preventDefault();
        setModalStep(2);
    };

    const handleSubmitOnStep2 = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const newLanguageFiles = removeUnusedVariablesFromEmailTemplateLanguageFiles(
            state.template,
            state.languages
        );

        const newStringifiedLanguageFiles: Partial<MultiLanguageWrapper<string>> = {};
        (Object.keys(newLanguageFiles) as Array<LocaleTypes>).forEach((key => {
            newStringifiedLanguageFiles[key] = JSON.stringify(newLanguageFiles[key]);
        }));

        const newEmailTemplate = await createEmailTemplateReduxRequest(dispatch)(
            state.template,
            state.name,
            state.company?.id,
            state.sender,  
            newStringifiedLanguageFiles,
            axnCompanies,
            addToReferenceData
        );
        if (newEmailTemplate) {
            if (onEmailTemplateCreated) {
                onEmailTemplateCreated(newEmailTemplate);
            }
            onModalClose();
        } else {
            setRequestInProgress(false);
        }
    }

    const handleBackClickedOnStep1 = () => {
        setModalStep(0);
    };

    const handleBackClickedOnStep2 = () => {
        setModalStep(1);
    };

    const validationErrors = useMemo(() => {
        return getEmailTemplateValidationErrors(state);
    }, [
        state.name, state.languages,
        state.template, state.templateAvailabilityLevel,
        state.company
    ]);

    return (
        <DefaultModal
            automationId="add-email-template-modal"
            ariaLabelledBy="add-email-template-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={modalStep > 0}
        >
            {modalStep === 0 && (
                <EmailTemplateMetadataForm
                    session={session}
                    axnCompanies={axnCompanies}
                    selfServiceVerificationWhiteLabeledEmails= {selfServiceVerificationWhiteLabeledEmails}
                    validationErrors={validationErrors.generalInfo}
                    emailTemplates={emailTemplates}
                    serviceOrganizationId={serviceOrganizationId}
                    handleSubmit={handleSubmitOnStep0}
                    labelWidth={labelWidth}
                    onModalClose={onModalClose}
                    setState={setState}
                    state={state}
                    requestInProgress={requestInProgress}
                />
            )}
            {modalStep === 1 && (
                <EmailTemplateContentForm
                    state={state}
                    requestInProgress={requestInProgress}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    validationErrors={validationErrors.content}
                    setState={setState}
                    onBackClicked={handleBackClickedOnStep1}
                    handleSubmit={handleSubmitOnStep1}
                />
            )}
            {modalStep === 2 && (
                <EmailTemplateVariablesForm
                    state={state}
                    requestInProgress={requestInProgress}
                    selectedLanguage={selectedLanguage}
                    validationErrors={validationErrors.variables}
                    setSelectedLanguage={setSelectedLanguage}
                    setState={setState}
                    onBackClicked={handleBackClickedOnStep2}
                    handleSubmit={handleSubmitOnStep2}
                />
            )}
        </DefaultModal>
    );
};